/**
 * src/pages/Embudo.jsx
 * Embudo comercial: visitas landing → clics CTA → diagnósticos F1 → registros
 * → contrataciones, con tasas de conversión entre etapas, más consultas de
 * contacto y las listas operativas de diagnósticos/leads recientes.
 * (Analytics fase A2 — ver PLAN_ANALYTICS_EMBUDO.md)
 */
import { useState } from 'react'
import { Header } from '../components/layout/Header'
import { Card, KpiCard } from '../components/ui/Card'
import { Badge, RESULTADO_COLOR } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { useEmbudo } from '../hooks/useAdminPanel'

const RANGOS = [
  { dias: 7, label: 'Semana' },
  { dias: 30, label: 'Mes' },
  { dias: 365, label: 'Año' },
]

const ETAPAS = [
  { key: 'visitantes', label: 'Visitantes landing', color: 'var(--color-running)' },
  { key: 'clics_cta', label: 'Clics en CTA', color: 'var(--color-running)' },
  { key: 'diagnosticos', label: 'Diagnósticos solicitados', color: 'var(--color-warn)' },
  { key: 'registros', label: 'Registros (trial)', color: 'var(--color-accent)' },
  { key: 'contrataciones', label: 'Contrataciones', color: 'var(--color-ok)' },
]

function fmtFecha(iso) {
  if (!iso) return '—'
  return new Date(iso + 'Z').toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

function pct(parte, total) {
  if (!total) return null
  return Math.round((parte / total) * 1000) / 10
}

function Funnel({ embudo }) {
  const max = Math.max(1, ...ETAPAS.map(e => embudo[e.key] || 0))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {ETAPAS.map((etapa, i) => {
        const valor = embudo[etapa.key] || 0
        const previa = i > 0 ? (embudo[ETAPAS[i - 1].key] || 0) : null
        const conv = i > 0 ? pct(valor, previa) : null
        return (
          <div key={etapa.key}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', marginBottom: '4px',
              fontSize: '12px',
            }}>
              <span style={{ color: 'var(--color-text-2)' }}>{etapa.label}</span>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}>
                <strong>{valor}</strong>
                {conv != null && (
                  <span style={{ color: 'var(--color-text-3)', marginLeft: '8px' }}>
                    {conv}% de la etapa anterior
                  </span>
                )}
              </span>
            </div>
            <div style={{
              height: '22px', background: 'var(--color-surface-2)',
              borderRadius: '5px', overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.max(valor / max * 100, valor > 0 ? 2 : 0)}%`,
                height: '100%', background: etapa.color, borderRadius: '5px',
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

const DIAG_COLUMNS = [
  { key: 'creado_en', label: 'Fecha', mono: true, nowrap: true, width: '120px', render: fmtFecha },
  {
    key: 'empresa', label: 'Empresa',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v || '(sin nombre)'}</div>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.email}
        </div>
      </div>
    ),
  },
  {
    key: 'estado', label: 'Estado', width: '95px',
    render: v => <Badge color={RESULTADO_COLOR[v] || (v === 'pending' ? 'yellow' : 'gray')}>{v}</Badge>,
  },
  {
    key: 'enviado', label: 'Email', width: '80px', mono: true,
    render: v => v
      ? <span style={{ color: 'var(--color-ok)' }}>enviado</span>
      : <span style={{ color: 'var(--color-text-3)' }}>—</span>,
  },
]

const LEAD_COLUMNS = [
  { key: 'creado_en', label: 'Fecha', mono: true, nowrap: true, width: '120px', render: fmtFecha },
  {
    key: 'empresa', label: 'Empresa',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v || '(sin nombre)'}</div>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.email}
        </div>
      </div>
    ),
  },
  { key: 'origen', label: 'Origen', mono: true, width: '100px' },
]

const REGISTRO_COLUMNS = [
  { key: 'creado_en', label: 'Fecha', mono: true, nowrap: true, width: '120px', render: fmtFecha },
  {
    key: 'nombre', label: 'Usuario',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v || '(sin nombre)'}</div>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.email}
        </div>
      </div>
    ),
  },
  {
    key: 'estado_suscripcion', label: 'Suscripción', width: '120px',
    render: (v, row) => v
      ? <Badge color={v === 'active' ? 'green' : v === 'trialing' ? 'blue' : 'gray'} dot>
          {row.tier ? `${row.tier} · ` : ''}{v}
        </Badge>
      : <span style={{ color: 'var(--color-text-3)', fontSize: '12px' }}>sin plan</span>,
  },
]

const VISITANTE_COLUMNS = [
  {
    key: 'primera_hora', label: 'Cuándo', mono: true, nowrap: true, width: '130px',
    render: v => fmtFecha(v),
  },
  {
    key: 'visitante', label: 'Visitante', mono: true, width: '110px',
    render: v => <span title="Código anónimo (rota a diario)">#{v}</span>,
  },
  {
    key: 'paginas', label: 'Páginas vistas', mono: true,
    render: v => v?.length ? v.join(' · ') : '—',
  },
  {
    key: 'ctas', label: 'CTAs clicados',
    render: v => v?.length
      ? v.map(c => <Badge key={c} color="blue">{c}</Badge>).reduce((a, b) => [a, ' ', b])
      : <span style={{ color: 'var(--color-text-3)', fontSize: '12px' }}>ninguno</span>,
  },
  {
    key: 'utm_source', label: 'Fuente', mono: true, width: '110px',
    render: v => v || <span style={{ color: 'var(--color-text-3)' }}>directa</span>,
  },
]

export default function Embudo() {
  const [dias, setDias] = useState(30)
  const { data, isLoading } = useEmbudo(dias)
  const embudo = data?.embudo || {}
  const tasaGlobal = pct(embudo.contrataciones || 0, embudo.visitantes || 0)

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Embudo"
        subtitle={`Conversión landing → contratación · captura desde ${data?.captura_desde || '2026-07-13'}`}
        action={
          <div style={{ display: 'flex', gap: '4px' }}>
            {RANGOS.map(r => (
              <button
                key={r.dias}
                onClick={() => setDias(r.dias)}
                style={{
                  border: '1px solid var(--color-border)',
                  background: dias === r.dias ? 'var(--color-accent-dim)' : 'var(--color-surface)',
                  color: dias === r.dias ? 'var(--color-accent)' : 'var(--color-text-2)',
                  borderRadius: '6px', padding: '5px 12px', cursor: 'pointer',
                  fontSize: '12px', fontFamily: 'var(--font-mono)',
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px',
          marginBottom: '24px',
        }}>
          <KpiCard label="VISITANTES" value={isLoading ? '—' : (embudo.visitantes ?? 0)}
                   color="var(--color-running)"
                   sub={`${embudo.pageviews ?? 0} páginas vistas · únicos/día`} />
          <KpiCard label="DIAGNÓSTICOS" value={isLoading ? '—' : (embudo.diagnosticos ?? 0)}
                   color="var(--color-warn)" sub={`últimos ${dias} días`} />
          <KpiCard label="CONSULTAS CONTACTO" value={isLoading ? '—' : (data?.consultas ?? 0)}
                   sub="formulario de la landing" />
          <KpiCard label="CONVERSIÓN GLOBAL" value={tasaGlobal != null ? `${tasaGlobal}%` : '—'}
                   color={tasaGlobal ? 'var(--color-ok)' : 'var(--color-text-3)'}
                   sub="visitante → contratación" />
        </div>

        <Card
          title="Embudo de conversión"
          subtitle={`Últimos ${dias} días · los datos de visitas acumulan desde el ${data?.captura_desde || '2026-07-13'}`}
        >
          {isLoading ? (
            <div style={{
              textAlign: 'center', padding: '32px', color: 'var(--color-text-3)',
              fontSize: '12px', fontFamily: 'var(--font-mono)',
            }}>
              Cargando…
            </div>
          ) : (
            <Funnel embudo={embudo} />
          )}
        </Card>

        <Card
          title="Recorridos de visitantes"
          subtitle="Conducta anónima por visitante y día: qué vio, qué clicó y de dónde vino. La identidad aparece cuando dejan sus datos (diagnóstico, contacto o registro) — por diseño no se rastrea a nadie sin su consentimiento (Ley 21.719)."
          padding="0"
          style={{ marginTop: '24px' }}
        >
          <Table columns={VISITANTE_COLUMNS} data={data?.visitantes_recientes} loading={isLoading}
                 emptyMessage="Sin visitas en el rango" />
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px', marginTop: '24px' }}>
          <Card title="Diagnósticos recientes" subtitle="Solicitudes del diagnóstico gratuito (F1)" padding="0">
            <Table columns={DIAG_COLUMNS} data={data?.diagnosticos_recientes} loading={isLoading}
                   emptyMessage="Sin diagnósticos aún" />
          </Card>
          <Card title="Consultas recientes" subtitle="Formulario de contacto de la landing" padding="0">
            <Table columns={LEAD_COLUMNS} data={data?.leads_recientes} loading={isLoading}
                   emptyMessage="Sin consultas aún" />
          </Card>
          <Card title="Registros recientes" subtitle="Cuentas creadas (identificados)" padding="0">
            <Table columns={REGISTRO_COLUMNS} data={data?.registros_recientes} loading={isLoading}
                   emptyMessage="Sin registros aún" />
          </Card>
        </div>
      </div>
    </div>
  )
}
